import React from 'react';
import { Autocomplete, CircularProgress, Popper, TextField } from '@mui/material';
import useGetAllLanguages from '../../hooks/languages/useGetAllLanguages';
import { Language } from '../../../types/language-types';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from './DialogGlynt';
import DialogTitleGlynt from './DialogTitleGlynt';
import DialogContentGlynt from './DialogContentGlynt';
import DialogActionsGlynt from './DialogActionsGlynt';

interface ISetLanguageDialogProps {
  open: boolean;
  currentLanguageCode?: string | null;
  isUpdating: boolean;
  onClose: () => void;
  onSubmit: (languageCode: string | null) => void;
}

const SetLanguageDialog: React.FC<ISetLanguageDialogProps> = ({
  open,
  currentLanguageCode,
  isUpdating,
  onClose,
  onSubmit,
}) => {
  const [selectedLanguageCode, setSelectedLanguageCode] = React.useState<string | null>(
    currentLanguageCode ?? null
  );

  const { data: languages, isLoading } = useGetAllLanguages();

  // Reset selected language when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedLanguageCode(currentLanguageCode ?? null);
    }
  }, [open, currentLanguageCode]);

  const handleSubmit = () => {
    onSubmit(selectedLanguageCode);
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      width="500px"
      cypressId="dialog-set-language"
    >
      <DialogTitleGlynt title="Set Language" closeModal={onClose} />
      <DialogContentGlynt>
        <FormControlStyled fullWidth>
          <FormLabelStyled>LANGUAGE</FormLabelStyled>
          <Autocomplete
            fullWidth
            options={languages || []}
            value={(languages || []).find((l) => l.code === selectedLanguageCode) || null}
            onChange={(event, newValue: Language | null) => {
              setSelectedLanguageCode(newValue ? newValue.code : null);
            }}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            getOptionLabel={(option) => option.name}
            disabled={isLoading}
            PopperComponent={(props) => (
              <Popper data-cy="popper-select-language-popup" {...props} placement="bottom-start" />
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Search language"
                data-cy="select-language"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControlStyled>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="btn-set-language-cancel"
        submitCypressId="btn-set-language-submit"
        clearCypressId="btn-set-language-clear"
        submitButtonText="Submit"
        submitLoading={isUpdating}
        showCancelButton={true}
        showClearButton={false}
        submitDisabled={isUpdating}
      />
    </DialogGlynt>
  );
};

export default SetLanguageDialog;
